import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PhoneNumberDropdownItem } from '@csspension/reactive-form/lib/components/reactive-form-item/reactive-form-phone-country-code/phone-number-item';
import { PhoneCountryCodeService } from '../phone-country-code.service';
import { map } from 'rxjs/operators';
import { NgbDropdown, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { ScreenService } from '../../../../../../services/screen-service.service';
import { Subscribable } from '../../../../../../models/base/subscribable';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';

@Component({
  selector: 'app-phone-country-code-dropdown-input',
  templateUrl: './phone-country-code-dropdown-input.component.html',
  styleUrls: ['./phone-country-code-dropdown-input.component.scss'],
  standalone: true,
  imports: [CommonModule, NgbDropdownModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneCountryCodeDropdownInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhoneCountryCodeDropdownInputComponent
  extends Subscribable
  implements ControlValueAccessor, OnChanges, OnInit, AfterViewInit
{
  @Input() countryDropdowns: PhoneNumberDropdownItem[] = [];
  @ViewChild('ngbDropdown') ngbDropdown!: NgbDropdown;

  protected cdr = inject(ChangeDetectorRef);
  protected phoneCountryCodeService = inject(PhoneCountryCodeService);
  protected screenService = inject(ScreenService);
  public value: string = '';
  public selectedCountry: PhoneNumberDropdownItem | null = null;
  public isDisabled: boolean = false;

  protected _dropdownIsOpen = new BehaviorSubject<boolean>(false);
  public dropdownIsOpen$ = this._dropdownIsOpen as Observable<boolean>;

  protected _isHovered = new BehaviorSubject<boolean>(false);
  public isHovered$ = this._isHovered as Observable<boolean>;
  public setIsHovered = (isHovered: boolean) => {
    this._isHovered.next(isHovered);
  };

  public interactionState$ = this.phoneCountryCodeService.interactionState$;

  public iconSrc$ = combineLatest([this.dropdownIsOpen$, this.isHovered$]).pipe(
    map(([dropdownIsOpen, isHovered]) => {
      if (dropdownIsOpen) {
        return 'assets/icons/black/Chevron Up.svg';
      }
      if (isHovered) {
        return 'assets/icons/black/Chevron Down.svg';
      }
      return 'assets/icons/grey/Chevron Down.svg';
    })
  );

  protected onChange: (value: string) => void = () => {};
  protected onTouched: () => void = () => {};

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.countryDropdowns) {
      this.syncSelectedCountry(this.value);
    }
  }

  public ngOnInit() {
    this.screenService.isMobile$.subscribeWhileAlive({
      owner: this,
      next: isMobile => {
        if (isMobile) {
          this.ngbDropdown?.close();
        }
      }
    });
  }

  public ngAfterViewInit(): void {
    this.ngbDropdown.openChange.subscribeWhileAlive({
      owner: this,
      next: (isOpen: boolean) => {
        this._dropdownIsOpen.next(isOpen);
      }
    });
  }

  public onFocus() {
    this.phoneCountryCodeService.setIsFocused(true);
  }

  public onBlur() {
    this.phoneCountryCodeService.setIsFocused(false);
  }

  public writeValue(value: string): void {
    this.value = value;
    this.syncSelectedCountry(value);
    this.cdr.detectChanges();
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  public syncSelectedCountry(value: string): void {
    this.selectedCountry = this.countryDropdowns?.find(option => option.countryCode === value) || null;
  }

  public onSelectChange(event: Event): void {
    const selectedCode = (event.target as HTMLSelectElement).value;
    const country = this.countryDropdowns.find(item => item.countryCode === selectedCode);
    if (country) {
      this.onCountrySelected(country);
    }
  }

  public onCountrySelected(country: PhoneNumberDropdownItem) {
    this.value = country.countryCode;
    this.selectedCountry = country;
    this.onChange(this.value);
    this.onTouched();
    this.cdr.detectChanges();
  }
}
